import Vue from "vue";
import appStore from "@/store/appStore";
import i18n from "@/plugins/i18n";
import "abortcontroller-polyfill/dist/abortcontroller-polyfill-only";
import { uriWithQueryStringParams } from "../utils/urlUtils";
const axios = require("axios");

const baseURL = "/api/";
let currentAbortControllers = [];

export const get = async (
  url = "",
  requestOverrides,
  enforceSingleRequestKey
) => {
  return await fetchInternal(
    url,
    "GET",
    {},
    requestOverrides,
    enforceSingleRequestKey
  );
};

export const post = async (
  url = "",
  body = {},
  requestOverrides,
  enforceSingleRequestKey
) => {
  return await fetchInternal(
    url,
    "POST",
    body,
    requestOverrides,
    enforceSingleRequestKey
  );
};

export const put = async (
  url = "",
  body = {},
  requestOverrides,
  enforceSingleRequestKey
) => {
  return await fetchInternal(
    url,
    "PUT",
    body,
    requestOverrides,
    enforceSingleRequestKey
  );
};

export const remove = async (
  url = "",
  body = {},
  requestOverrides,
  enforceSingleRequestKey
) => {
  return await fetchInternal(
    url,
    "DELETE",
    body,
    requestOverrides,
    enforceSingleRequestKey
  );
};

const fetchInternal = async (
  url,
  method,
  body,
  requestOverrides,
  enforceSingleRequestKey
) => {
  const requestObject = {
    url: `${baseURL}${url}`,
    method,
    headers: {
      "Content-Type": "application/json"
    },
    data: body,
    ...requestOverrides
  };

  if (enforceSingleRequestKey) {
    requestObject.signal = setupAbortableRequest(enforceSingleRequestKey);
  }

  const requestTimestamp = Date.now();
  let apiResponse;

  await axios(requestObject)
    .then((response) => {
      apiResponse = response.data;
    })
    .catch((error) => {
      handleError(error);
    });

  try {
    const e = document.createEvent("CustomEvent");
    e.initCustomEvent("apiRequestTime", false, false, {
      requestTime: requestTimestamp
    });
    window.dispatchEvent(e);
  } catch {
    window.dispatchEvent(
      new CustomEvent("apiRequestTime", {
        detail: { requestTime: requestTimestamp }
      })
    );
  }

  if (enforceSingleRequestKey) {
    currentAbortControllers = currentAbortControllers.filter(
      (abortController) => abortController.key !== enforceSingleRequestKey
    );
  }

  return apiResponse;
};

function setupAbortableRequest(enforceSingleRequestKey) {
  const matchingAbortControllers = currentAbortControllers.filter(
    (abortController) => abortController.key === enforceSingleRequestKey
  );

  if (matchingAbortControllers.length) {
    matchingAbortControllers.forEach((abortController) =>
      abortController.abort()
    );
  }

  const abortController = new AbortController();
  abortController.key = enforceSingleRequestKey;
  currentAbortControllers.push(abortController);

  return abortController.signal;
}

function handleError(error) {
  // Treat axios cancellations consistently
  if (
    error?.code === "ERR_CANCELED" ||
    error?.message === "canceled" ||
    axios.isCancel?.(error)
  ) {
    throw "Request canceled";
  }

  const status = error.response?.status;

  // Redirect to login with a safely encoded returnUrl (pathname + search + hash)
  if (status === 401 && window.location.pathname.toLowerCase() !== "/login") {
    const returnUrl = `${window.location.pathname}${window.location.search}${
      window.location.hash || ""
    }`;
    window.location.href = uriWithQueryStringParams("/login", { returnUrl });
    return; // prevent fallthrough
  }

  if (status === 403) {
    Vue.prototype.$appInsights?.trackEvent({
      name: "User Received Forbidden Permissions"
    });

    appStore.commit("application/SET_ERROR", {
      message: i18n.t("errors.forbidden-permissions")
    });
  }

  throw status;
}
